import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { BoxV2, Checkbox } from 'sonata-react-components';
import { DeviceMakeModelBidderDashboardResponse } from 'sonata-static-storage/lib/common/common.types';

import { Question } from '@/components/Targeting/Device/Question';
import { MakeModelsStyled } from './MakeModels.styled';
import { MakeModelsSelection } from './MakeModelsSelection';

import type { DeviceMakeModelType } from 'sonata-static-storage/lib/schemas/targeting/DeviceMakeModel/DeviceMakeModels.types';

type Props = {
  getDeviceMakeModels: () => Promise<DeviceMakeModelBidderDashboardResponse[]>;
  id: string;
  makeModels: DeviceMakeModelType[];
  omitQuestion?: boolean;
  onMakeModelsChanged: (selectedDeviceMakeModels: DeviceMakeModelType[]) => void;
  onUnknownIncludedToggled: (unknownIncluded: boolean) => void;
  unknownIncluded: boolean;
  unknownIncludedPermission: boolean;
};

type State = {
  deviceMakeModelsEnabled: boolean;
};

export const MakeModels: React.FC<Props> = ({
  getDeviceMakeModels,
  id,
  makeModels,
  omitQuestion,
  onMakeModelsChanged,
  onUnknownIncludedToggled,
  unknownIncluded,
  unknownIncludedPermission
}) => {
  const { formatMessage } = useIntl();

  const [{ deviceMakeModelsEnabled }, setState] = useState<State>({
    deviceMakeModelsEnabled: makeModels.length > 0 || Boolean(omitQuestion)
  });

  const handlePacingQuestionChanged = (newStatus: boolean) => {
    onMakeModelsChanged?.([]);
    setState(prevState => ({
      ...prevState,
      deviceMakeModelsEnabled: newStatus
    }));
  };

  const handleMakeModelsOnChanged = (newMakeModels: DeviceMakeModelType[]) => {
    onMakeModelsChanged?.(newMakeModels);
  };

  const handleUnknownIncludedToggled = (_id: string, checked: boolean) => {
    onUnknownIncludedToggled(checked);
  };

  return (
    <MakeModelsStyled className="targetingDeviceMakeModelsContainer">
      <BoxV2 id={`${id}_make_models_box_container`}>
        <BoxV2.Body bgColor="transparent" padding="0">
          {!omitQuestion && (
            <Question
              deviceMakeModelsEnabled={deviceMakeModelsEnabled}
              disabledText={formatMessage({
                defaultMessage: 'Show ads across all device makers',
                id: 'targetingDeviceMakeModels.question.disabled'
              })}
              enabledText={formatMessage({
                defaultMessage: 'Target specific device maker',
                id: 'targetingDeviceMakeModels.question.enabled'
              })}
              handlePacingQuestionChanged={handlePacingQuestionChanged}
              id={`${id}_make_models_targeting_question`}
            />
          )}
          {deviceMakeModelsEnabled && (
            <MakeModelsSelection
              getDeviceMakeModels={getDeviceMakeModels}
              id={`${id}_make_models_selector`}
              makeModels={makeModels}
              onChangeMakeModels={handleMakeModelsOnChanged}
            />
          )}
          {unknownIncludedPermission && makeModels.length > 0 && (
            <div className="targetingDeviceMakeModelsContainer__unknownIncluded">
              <Checkbox
                defaultActive={unknownIncluded}
                id={`${id}_unknownIncluded`}
                labelText="Unknown Included"
                onClickAction={handleUnknownIncludedToggled}
              />
            </div>
          )}
        </BoxV2.Body>
      </BoxV2>
    </MakeModelsStyled>
  );
};
